/**
 * @file src/core/PanelManager.js
 * @description Orchestrates panel activation logic for the main interaction modes (MIDI, Sensors, etc.)
 * and coordinates sensor/MIDI/webRTC side effects when panels change.
 *
 * De-singletonization: `PanelManager` is now a PER-VOICE instance, not a module
 * singleton. Each orbiter voice owns its own panel state (`currentPanel`) and broadcasts panel changes
 * on its OWN `eventBus` (the per-voice EventTarget), so opening a panel in one tile does not move every
 * other tile. Single-orbiter constructs exactly ONE instance whose `eventBus` defaults to `window` and
 * whose `root` defaults to `document`, so its behaviour is byte-identical to the old singleton.
 *
 * Single-focus surfaces (MIDI, CosmicLFO) are the ACTIVE-VOICE tier: they read
 * `voiceRegistry.getActive().panelManager` and listen to `subscribeToAnyPanelChange` (a realm-level
 * notification fired by every instance) instead of importing a singleton.
 */
import { SensorController } from '../input/SensorsController.js';
import notifications from './AppNotifications.js';
import { MIDIControllerInstance } from '../input/midi/MIDIController.js';
import { INTERNAL_SENSORS_USABLE, EXTERNAL_SENSORS_USABLE, MIDI_SUPPORTED } from '../config/Constants.js';
import { WebRTCManager } from '../api/WebRTCManager.js';
import { getT } from '../i18n/index.js';
import { voiceRegistry } from '../voice/VoiceRegistry.js';
import { isApplyingRemotePanelMirror } from '../room/roomPanelBridge.js';

const PANEL_IDS = {
    JAMMING: 'JAMMING_PANEL',
    MIDI: 'MIDI_PANEL',
    SENSORS: 'SENSORS_PANEL',
    COSMIC_LFO: 'COSMIC_LFO_PANEL',
    PLAYBACK: 'PLAYBACK_PANEL'
};

const PANEL_CHANGE_EVENT = 'orbiters:panel-change';

const PANEL_LABEL_KEYS = {
    [PANEL_IDS.JAMMING]: 'panels.jamming',
    [PANEL_IDS.MIDI]: 'panels.midi',
    [PANEL_IDS.SENSORS]: 'panels.sensors',
    [PANEL_IDS.COSMIC_LFO]: 'panels.cosmicLfo',
    [PANEL_IDS.PLAYBACK]: 'panels.playback'
};

let t = getT();

function getPanelLabel(panelId) {
    const key = PANEL_LABEL_KEYS[panelId];
    if (!key) {
        return panelId?.toLowerCase?.().replace('_', ' ') || '';
    }
    return t(key);
}

/**
 * Realm-level "a panel changed somewhere" notification, for single-focus surfaces (MIDI) that need to
 * refresh on the FOCUSED voice's panel changes without owning a per-voice subscription. This is the
 * active-voice tier — NOT panel state (which is per-voice on each instance below).
 */
const anyPanelChangeObservers = new Set();
export function subscribeToAnyPanelChange(cb) {
    if (typeof cb !== 'function') return () => {};
    anyPanelChangeObservers.add(cb);
    return () => anyPanelChangeObservers.delete(cb);
}
function notifyAnyPanelChange(panelId) {
    anyPanelChangeObservers.forEach((cb) => {
        try { cb(panelId); } catch (error) { console.error('[PanelManager] any-panel-change observer error:', error); }
    });
}

/** The panel manager of the focused voice (active-voice tier), or null. */
function activePanelManager() {
    return voiceRegistry.getActive()?.panelManager ?? null;
}

/**
 * Handles registering panels, switching between them, and notifying subscribers when this voice's
 * active UI mode changes. One instance PER VOICE.
 */
class PanelManager {
    /**
     * @param {object} [opts]
     * @param {EventTarget|Window} [opts.eventBus] where panel-change is dispatched (per-voice EventTarget
     *   for a multi tile; defaults to `window` → single-orbiter byte-identical).
     * @param {Document|HTMLElement} [opts.root] DOM scope for this voice's chrome (defaults to `document`).
     * @param {string} [opts.voiceId] the realm-registry id of this voice, so its CameraController can gate
     *   pointer input on focus. null/undefined = single-orbiter (always the active voice).
     */
    constructor({ eventBus, root, voiceId } = {}) {
        this.voiceId = voiceId ?? null;
        this.currentPanel = null;
        this.subscribers = [];
        this.panels = {};
        this.sensorControllerInstance = null;
        // Set by Interaction.js (the Sensors panel reads it to build its SensorController).
        this.parameterManager = null;
        this.eventBus = eventBus ?? (typeof window !== 'undefined' ? window : null);
        this.root = root ?? (typeof document !== 'undefined' ? document : null);
        registerDefaultPanels(this);
    }

    registerPanel(id, { onEnter, onExit }) {
        this.panels[id] = { onEnter, onExit };
    }

    activatePanel(newPanel) {
        if (this.currentPanel === newPanel) {
            return;
        }

        const oldPanel = this.currentPanel;
        if (this.panels[oldPanel]?.onExit) {
            try {
                this.panels[oldPanel].onExit();
            } catch (error) {
                console.error(`[PanelManager] Error exiting panel "${oldPanel}":`, error);
            }
        }

        this.currentPanel = newPanel;

        // Gate this voice's camera drive on the new panel (jamming = on, else off).
        this._applyCameraDriveFilter(newPanel);

        const titleElement = document.querySelector('.responsive-title');
        if (titleElement) {
            titleElement.textContent = getPanelLabel(newPanel);
        }

        if (this.panels[newPanel]?.onEnter) {
            try {
                this.panels[newPanel].onEnter();
            } catch (error) {
                console.error(`[PanelManager] Error entering panel "${newPanel}":`, error);
            }
        } else {
            console.warn(`[PanelManager] No onEnter handler for panel: ${newPanel}`);
        }

        this.subscribers.forEach((cb) => {
            try {
                cb(newPanel);
            } catch (error) {
                console.error('[PanelManager] Subscriber error:', error);
            }
        });

        // Per-voice: React's `panels` facade subscribes to THIS voice's eventBus (window for single-orbiter).
        if (this.eventBus?.dispatchEvent) {
            this.eventBus.dispatchEvent(new CustomEvent(PANEL_CHANGE_EVENT, {
                detail: { panelId: newPanel, previousPanel: oldPanel }
            }));
        }
        // Active-voice surfaces (MIDI) refresh on any voice's panel change.
        notifyAnyPanelChange(newPanel);
    }

    subscribe(callback) {
        this.subscribers.push(callback);
    }

    getActivePanel() {
        return this.currentPanel;
    }

    // Panel state is a drive FILTER over the voice's persistent CameraController (created by the
    // voice session at boot, disposed with the voice), NOT a create/destroy trigger. Camera drag/zoom/
    // recenter act only in jamming mode; focus-on-pointerdown stays live in every panel (the controller
    // owns that). `this.voiceId` is always the voice's registry id, so no getActive() lookup.
    _applyCameraDriveFilter(panel) {
        const cam = voiceRegistry.get(this.voiceId)?.cameraController;
        cam?.enablePointerParamDrive(panel === PANEL_IDS.JAMMING);
    }
}

/**
 * Register the standard panels onto a PanelManager instance. Called from the constructor so every
 * per-voice instance carries the same handler set (the handlers close over `pm`, not a singleton).
 */
function registerDefaultPanels(pm) {
    pm.registerPanel(PANEL_IDS.JAMMING, {
        // Nothing to set up — the panel's one side effect (camera drive) is applied by
        // `activatePanel` on every panel change (see `_applyCameraDriveFilter`).
        onEnter: () => {}
        // No onExit — the camera controller lives with the voice, not the jamming panel.
    });

    pm.registerPanel(PANEL_IDS.MIDI, {
        onEnter: async () => {
            // A room mirror lands the deck on this panel for everyone — but MIDI ACCESS (a browser
            // permission prompt on some browsers) belongs to a local gesture only. The mirror shows
            // where the deck is; each viewer activates their own MIDI when THEY press for it.
            if (isApplyingRemotePanelMirror()) return;
            try {
                if (MIDI_SUPPORTED && MIDIControllerInstance) {
                    if (!MIDIControllerInstance.isMIDIActivated) {
                        await MIDIControllerInstance.activateMIDI();
                    }
                    await MIDIControllerInstance.loadPersistedMappings();
                } else {
                    const tt = getT();
                    notifications.showToast(tt('notifications.midiActivationError'), 'error');
                }
            } catch (error) {
                console.error('[PanelManager] Error entering MIDI panel:', error);
                const tt = getT();
                notifications.showToast(tt('notifications.midiActivationError'), 'error');
            }
        },
        onExit: () => {}
    });

    pm.registerPanel(PANEL_IDS.SENSORS, {
        onEnter: async () => {
            // A room mirror shows WHERE the deck is — it must never start THIS device's sensor
            // acquisition. The iOS motion-permission prompt and the phone-pairing dialog open only
            // from a local gesture; a viewer who wants their own sensors presses for them here.
            // (The latch is only valid synchronously, so it is read before any await.)
            if (isApplyingRemotePanelMirror()) return;

            if (!pm.parameterManager) {
                const tt = getT();
                notifications.showToast(tt('notifications.sensorsUnavailable'), 'error');
                return;
            }

            try {
                const sensorController = SensorController.getInstance(pm.parameterManager);
                const webRTCManager = WebRTCManager.getInstance((data) => {
                    sensorController.setExternalSensorData(data);
                });

                if (INTERNAL_SENSORS_USABLE && SensorController.isSupported()) {
                    const permissionGranted = await sensorController.requestPermission();
                    if (permissionGranted) {
                        await sensorController.activateSensors();
                        sensorController.switchSensorSource(false);
                    } else {
                        const tt = getT();
                        notifications.showToast(tt('notifications.sensorPermissionDenied'), 'error');
                    }
                } else if (EXTERNAL_SENSORS_USABLE) {
                    try {
                        await webRTCManager.initialize();
                        // Asking for sensors with no phone yet paired IS the request to pair, and there
                        // is no device button to press yet — so offer the QR. Once a device has paired
                        // this session the panel stays quiet: the button is there, and a dialog that
                        // opens itself over a playing instrument forces you to stop to dismiss it.
                        if (!webRTCManager.hasPairedThisSession) {
                            webRTCManager.generateConnectionModal(true);
                        }
                        // Only claim sensors are live when a device actually IS live. Announcing
                        // success on every visit told a user whose phone had just dropped that
                        // everything was fine, while nothing drove the axes.
                        if (webRTCManager.isConnected) {
                            const tt = getT();
                            notifications.showToast(tt('notifications.externalSensorsActivated'), 'success');
                        }
                    } catch (error) {
                        console.error('[PanelManager] Failed to initialize WebRTCManager.', error);
                        const tt = getT();
                        notifications.showToast(tt('notifications.externalSensorsFailed'), 'error');
                    }
                } else {
                    const tt = getT();
                    notifications.showToast(tt('notifications.noSensorsAvailable'), 'warning');
                }

                pm.sensorControllerInstance = sensorController;
            } catch (error) {
                console.error('[PanelManager] Error during sensor activation:', error);
                const tt = getT();
                notifications.showToast(tt('notifications.sensorsUnavailable'), 'error');
            }
        },
        onExit: () => {
            const sc = pm.sensorControllerInstance;
            const anyAxisActive =
                sc && (sc.isSensorActive || Object.values(sc.activeAxes || {}).some(Boolean));

            if (sc && !anyAxisActive) {
                try {
                   // sc.deactivateSensors();
                } catch (error) {
                    console.error('[PanelManager] Error deactivating sensors:', error);
                }
                pm.sensorControllerInstance = null;
            }

            // Leaving the panel says nothing about whether this device CAN host an external sensor,
            // so nothing about sensor availability is written here — it used to be, and that single
            // shared write is what killed pairing for every orbiter on the page.
        }
    });

    pm.registerPanel(PANEL_IDS.COSMIC_LFO, {
        onEnter: () => {
            const cosmicLFOManager = voiceRegistry.getActive()?.cosmicLFOManager;
            if (cosmicLFOManager) {
                cosmicLFOManager.x.enterMode();
                cosmicLFOManager.y.enterMode();
                cosmicLFOManager.z.enterMode();
            }
        }
        // No onExit — leaving the panel neither stops the LFOs (the enable is owned by the React
        // cosmic surface) nor has any view to tear down.
    });

    pm.registerPanel(PANEL_IDS.PLAYBACK, {
        onEnter: () => {},
        onExit: () => {}
    });
}

if (typeof window !== 'undefined') {
    window.addEventListener('languageChanged', () => {
        t = getT();
        const activePanel = activePanelManager()?.getActivePanel?.();
        if (!activePanel) return;
        const titleElement = document.querySelector('.responsive-title');
        if (titleElement) {
            titleElement.textContent = getPanelLabel(activePanel);
        }
    });
}

/**
 * Initialize the default panel state for a voice's PanelManager. Takes the instance explicitly (no
 * singleton): activates the default panel for THIS voice. (`panelManager.parameterManager` is set
 * by the caller for the Sensors panel.)
 */
function initializePanels(panelManager, defaultPanel = PANEL_IDS.JAMMING) {
    panelManager.activatePanel(defaultPanel);
}

export { PanelManager, PANEL_IDS, initializePanels };

